import type { Y } from '@piccolohealth/pbs-common';
import { ChakraV3, ScrollArea } from '@piccolohealth/ui';
import { inflection, P } from '@piccolohealth/util';
import React from 'react';
import { showModal } from '../../../components/generic/Modal';
import { useScoringChecklist } from '../hooks/useScoringChecklist';
import { TemplateScoringChecklistDeleteModal } from './TemplateScoringChecklistDeleteModal';

interface Props {
  yDoc: Y.Doc;
}

export const TemplateScoringChecklistSidebar = (props: Props) => {
  const { yDoc } = props;

  const { checklist, deleteChecklist } = useScoringChecklist({ yDoc });

  const { categoryCount, criteriaCount } = React.useMemo(() => {
    const categories = checklist?.categories ?? [];
    const criteria = categories.flatMap((c) => c.criterias);

    return {
      categoryCount: categories.length,
      criteriaCount: criteria.length,
    };
  }, [checklist]);

  const labelCounts = React.useMemo(() => {
    if (!checklist) {
      return [];
    }

    const criteria = checklist.categories.flatMap((category) => category.criterias);
    const criteriaLabels = checklist.criteriaLabels || [];

    return criteriaLabels
      .map((label) => {
        const count = criteria.filter((c) => c.labelIds.includes(label.id)).length;

        return {
          name: label.name,
          color: label.color,
          count,
        };
      })
      .filter((labelCount) => labelCount.count > 0);
  }, [checklist]);

  const content = P.run(() => {
    if (!checklist) {
      return (
        <ChakraV3.Stack px={3} py={2} gap={2}>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            No scoring checklist created.
          </ChakraV3.Text>
        </ChakraV3.Stack>
      );
    }

    return (
      <ChakraV3.Stack px={3} py={2} gap={4}>
        <ChakraV3.Stack>
          <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
            Overview
          </ChakraV3.Text>
          <ChakraV3.HStack fontSize='sm' color='secondary' gap={1}>
            {categoryCount} {inflection.inflect('category', categoryCount)},{criteriaCount}{' '}
            {inflection.inflect('criteria', criteriaCount)}
          </ChakraV3.HStack>
        </ChakraV3.Stack>

        <ChakraV3.Stack>
          <ChakraV3.Text fontSize='sm' fontWeight='semibold'>
            Labels
          </ChakraV3.Text>
          <ChakraV3.Stack>
            {P.isEmpty(labelCounts) && (
              <ChakraV3.Text fontSize='sm' color='secondary'>
                No labels used
              </ChakraV3.Text>
            )}
            {labelCounts.map((l) => (
              <ChakraV3.HStack key={l.name} justify='space-between' align='center'>
                <ChakraV3.Tag size='sm' colorPalette={l.color}>
                  {l.name}
                </ChakraV3.Tag>
                <ChakraV3.Text fontSize='sm' color='secondary'>
                  {l.count} {inflection.inflect('criteria', l.count)}
                </ChakraV3.Text>
              </ChakraV3.HStack>
            ))}
          </ChakraV3.Stack>
        </ChakraV3.Stack>

        <ChakraV3.Separator />

        <ChakraV3.Stack>
          <ChakraV3.Button
            onClick={() =>
              showModal(TemplateScoringChecklistDeleteModal, {
                onDelete: deleteChecklist,
              })
            }
            size='2xs'
            variant='subtle'
            color='danger'
          >
            Delete checklist
          </ChakraV3.Button>
        </ChakraV3.Stack>
      </ChakraV3.Stack>
    );
  });

  return (
    <ChakraV3.Stack h='full' gap={0}>
      <ChakraV3.Text px={3} pt={2} fontWeight='semibold'>
        Summary
      </ChakraV3.Text>
      <ScrollArea h='full' overflowY='auto'>
        {content}
      </ScrollArea>
    </ChakraV3.Stack>
  );
};
